import { isString } from '@art-suite/art-core-ts';
import { groupNamePresenter } from '@kaiizen/kai-presenters';
import { ScopeReportLineItem } from '@kaiizen/kai-reports';
import {
  getScanTaskOrUndefined,
  isTaskScoped,
  ScanGroupOptimized,
  ScanPartOptimized,
  ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';
import { zenum, ZodLessInfer } from '@kaiizen/zod-less';
import {
  getModifierFilterDisplayName,
  getModifierFilterTag,
  isModifierFilterKey,
  isValidModifierFilterKey,
  type ModifierFilterKey,
  toCanonicalModifierFilterKey,
} from '../orgSettings';
import { ScanEditingContextType } from './ScanEditingContext';

export const TaskFilterModeSchema = zenum(
  'allScoped',
  'changeOrder', // TODO: should include deleted taskIds as well as changed and added taskIds
  'editRequests',
  'closeout',
  'punchlist',
  'unassigned',
  'outOfStock',
  'tenantResponsibility',
  'deferred',
  'contractor',
  'trade',
  'group',
);

export type TaskFilterMode = ZodLessInfer<typeof TaskFilterModeSchema>;
export const TaskFilterModes = TaskFilterModeSchema.options;

/**
 * Either kind of filter the scope editor can hold: one of the fixed modes above, or a modifier an
 * org created (KAIDEV-4206).
 *
 * The enum stays closed on purpose. Its members are route params and the subjects of exhaustive
 * switches below; widening it to accept any string would turn both into silent no-ops. An org
 * modifier is addressed by tag instead, behind a prefix that cannot collide with a mode name.
 */
export type TaskFilterKey = TaskFilterMode | ModifierFilterKey;

/**
 * The modes that a modifier filter has replaced, and the tags they stood for.
 *
 * `subview=deferred` is in saved deep links, so it has to keep resolving. Both remain valid
 * modes; they are simply rewritten on the way in, so the dropdown — whose rows are keyed by tag
 * now — shows the right row as selected.
 */
const MODIFIER_TAG_BY_LEGACY_FILTER_MODE: Partial<Record<TaskFilterMode, string>> = {
  deferred: 'deferred',
  tenantResponsibility: 'tenantResponsibility',
};

export const toCanonicalTaskFilterKey = (key: TaskFilterKey): TaskFilterKey => toCanonicalModifierFilterKey(key, MODIFIER_TAG_BY_LEGACY_FILTER_MODE);

export const asValidTaskFilterMode = (
  value: unknown,
): TaskFilterMode | undefined =>
  isString(value) && TaskFilterModes.includes(value as TaskFilterMode)
    ? (value as TaskFilterMode)
    : undefined;

/**
 * Parses a route param into a filter key, rewriting the legacy modes to their modifier keys.
 *
 * A modifier key is accepted whatever tag it names: the org owns that vocabulary, and a key for a
 * modifier it has since deleted simply matches nothing.
 */
export const asValidTaskFilterKey = (
  value: unknown,
): TaskFilterKey | undefined => {
  if (!isString(value)) return undefined;
  if (isValidModifierFilterKey(value)) return value;
  const mode = asValidTaskFilterMode(value);
  return mode && toCanonicalTaskFilterKey(mode);
};

export type TaskFilterParams = {
  scopedOnly?: boolean;
  groupId?: string;
  contractorId?: string;
  trade?: string;
  taskFilterMode?: TaskFilterKey;
  filterRegex?: RegExp;
};

export type TaskIdWithGroupId = {
  taskId: string;
  groupId: string;
};

export const getTaskIdWithGroupIds = (
  taskIdToGroupId: Record<string, string>,
  taskIds: string[],
): TaskIdWithGroupId[] => {
  return taskIds.map(taskId => ({
    taskId,
    groupId: taskIdToGroupId[taskId],
  }));
};

export const filterScanPart = (
  scanPart: ScanPartOptimized | undefined,
  filterRegex: RegExp | undefined,
) =>
  !!scanPart
  && (!filterRegex || filterRegex.test(scanPart.optimized.searchText));

/**
 * The filter's own name, with no " scoped" suffix.
 *
 * `presentTaskFilterMode` adds that suffix for the filter banner, where it reads correctly. A
 * page title is not a banner: the scope editor's header shows the plain name for every fixed
 * mode, so a modifier has to use this rather than the suffixed form (KAIDEV-4206 review round 2).
 */
export const presentTaskFilterModeName = (
  scanEditingContext: ScanEditingContextType,
  {
    taskFilterMode,
    groupId,
    contractorId,
    trade,
    scopedOnly,
  }: TaskFilterParams,
): string => {
  const group = groupId
    ? scanEditingContext.scanRoot?.groupsById[groupId]
    : undefined;
  const contractor = contractorId
    ? scanEditingContext.contractorsMap?.[contractorId]
    : undefined;
  // A modifier's title is whatever the org named it, so it is looked up rather than switched on.
  if (taskFilterMode && isModifierFilterKey(taskFilterMode)) {
    return getModifierFilterDisplayName(taskFilterMode, scanEditingContext.knownTaskModifiers) ?? '';
  }
  switch (taskFilterMode ?? 'allScoped') {
    case 'group':
      return group ? groupNamePresenter(group) : '';
    case 'contractor':
      return contractor ? contractor.name : '';
    case 'trade':
      return trade ?? '';
    case 'allScoped':
    case 'closeout':
      return 'All Scoped';
    case 'changeOrder':
      return 'Change Order';
    case 'editRequests':
      return 'Edit Requests';
    case 'punchlist':
      return 'Punch List';
    case 'unassigned':
      return 'Unassigned';
    case 'outOfStock':
      return 'Out of Stock';
    // Fallbacks. A key that came through `asValidTaskFilterKey` is already a modifier key and is
    // titled from the org's settings above; these answer a caller that passed a raw mode.
    case 'tenantResponsibility':
      return 'Tenant Responsibility';
    case 'deferred':
      return 'Deferred';
    default:
      return '';
  }
};

export const presentTaskFilterMode = (
  scanEditingContext: ScanEditingContextType,
  taskFilterParams: TaskFilterParams,
): string => {
  const baseString = presentTaskFilterModeName(scanEditingContext, taskFilterParams);
  if (taskFilterParams.scopedOnly) {
    return `${baseString} scoped`;
  }
  return baseString;
};

export const getTaskFilterModeSelectedTasksIds = (
  scanEditingContext: ScanEditingContextType,
  {
    taskFilterMode,
    groupId,
    contractorId,
    trade,
  }: {
    taskFilterMode?: TaskFilterKey;
    groupId?: string;
    contractorId?: string;
    trade?: string;
  },
): string[] => {
  // Keyed by tag, so an org's own modifiers resolve by the same path as the two reserved ones.
  if (taskFilterMode && isModifierFilterKey(taskFilterMode)) {
    return scanEditingContext.taskIdsByModifierTag?.[getModifierFilterTag(taskFilterMode)] ?? [];
  }
  switch (taskFilterMode ?? 'allScoped') {
    case 'group':
      return groupId
        ? (scanEditingContext.scanRoot?.groupsById[groupId]?.tasks?.map(
          task => task.id,
        ) ?? [])
        : [];
    case 'contractor':
      return contractorId
        ? (scanEditingContext.taskIdsByAssignedContractorId?.[contractorId]
          ?? [])
        : [];
    case 'allScoped':
    case 'closeout':
      return scanEditingContext.scopedTaskIds;
    case 'changeOrder':
      return scanEditingContext.changedScopedTaskIds;
    // Fallbacks, as above: a canonicalized key takes the modifier branch before reaching here.
    case 'deferred':
      return scanEditingContext.deferredTaskIds;
    case 'editRequests':
      return scanEditingContext.editRequestTaskIds;
    case 'outOfStock':
      return scanEditingContext.outOfStockTaskIds;
    case 'punchlist':
      return scanEditingContext.needsReworkTaskIds;
    case 'tenantResponsibility':
      return scanEditingContext.tenantResponsibilityTaskIds;
    case 'trade':
      return trade ? (scanEditingContext.taskIdsByTrade?.[trade] ?? []) : [];
    case 'unassigned':
      return scanEditingContext.unassignedTaskIds;
    default:
      return [];
  }
};

export const getRegexFilteredTaskIds = (
  scanEditingContext: ScanEditingContextType,
  taskIds: string[],
  filterRegex?: RegExp,
): string[] => {
  if (!filterRegex) return taskIds;
  return filterRegex
    ? taskIds.filter(taskId =>
      filterScanPart(
        getScanTaskOrUndefined(
          scanEditingContext.scanRoot,
          scanEditingContext.taskIdToGroupId[taskId],
          taskId,
        ),
        filterRegex,
      )
    )
    : taskIds;
};

export const getScopedTaskIds = (
  scanEditingContext: ScanEditingContextType,
  taskIds: string[],
): string[] =>
  taskIds.filter(taskId =>
    isTaskScoped(
      getScanTaskOrUndefined(
        scanEditingContext.scanRoot,
        scanEditingContext.taskIdToGroupId[taskId],
        taskId,
      ),
    )
  );

export const getSelectedTasksIds = (
  scanEditingContext: ScanEditingContextType,
  {
    groupId,
    contractorId,
    trade,
    taskFilterMode,
    filterRegex,
  }: TaskFilterParams,
): string[] =>
  getRegexFilteredTaskIds(
    scanEditingContext,
    getTaskFilterModeSelectedTasksIds(scanEditingContext, {
      taskFilterMode,
      groupId,
      contractorId,
      trade,
    }),
    filterRegex,
  );

export const getSelectedTasksWithGroupIds = (
  scanEditingContext: ScanEditingContextType,
  taskFilterParams: TaskFilterParams,
): TaskIdWithGroupId[] => {
  return getTaskIdWithGroupIds(
    scanEditingContext.taskIdToGroupId,
    getSelectedTasksIds(scanEditingContext, taskFilterParams),
  );
};

export const getTasksById = (
  { scanRoot }: Pick<ScanEditingContextType, 'taskIdToGroupId' | 'scanRoot'>,
  taskIdWithGroupIds: TaskIdWithGroupId[],
  scopedOnly?: boolean,
) => {
  if (!scanRoot) return [];
  const tasks: ScanTaskOptimized[] = [];
  for (const { taskId, groupId } of taskIdWithGroupIds) {
    const group = scanRoot.groupsById[groupId];
    if (!group) continue;
    const task = group.tasksById[taskId];
    if (!task) continue;
    if (task && (!scopedOnly || isTaskScoped(task))) tasks.push(task);
  }
  return tasks;
};

export const getTaskIdsByGroupId = (
  taskIdWithGroupIds: TaskIdWithGroupId[],
): Record<string, string[]> => {
  const taskIdsByGroupId: Record<string, string[]> = {};
  for (const { taskId, groupId } of taskIdWithGroupIds) {
    (taskIdsByGroupId[groupId] ??= []).push(taskId);
  }
  return taskIdsByGroupId;
};

export type FilteredTaskAndGroupIds = {
  taskIds: Set<string>;
  groupIds: Set<string>;
};

export const getFilteredTaskAndGroupIds = (
  scanEditingContext: ScanEditingContextType,
  taskFilterParams: TaskFilterParams,
): FilteredTaskAndGroupIds => {
  const taskIds = new Set<string>();
  const groupIds = new Set<string>();
  for (
    const { taskId, groupId } of getSelectedTasksWithGroupIds(
      scanEditingContext,
      taskFilterParams,
    )
  ) {
    taskIds.add(taskId);
    groupIds.add(groupId);
  }
  return { taskIds, groupIds };
};

const filterScanReportLineItem = (
  lineItem: ScopeReportLineItem,
  filteredTaskAndGroupIds: FilteredTaskAndGroupIds,
): boolean => {
  if (lineItem.taskId) {
    return filteredTaskAndGroupIds.taskIds.has(lineItem.taskId);
  }
  if (lineItem.groupId) {
    return filteredTaskAndGroupIds.groupIds.has(lineItem.groupId);
  }
  return true;
};

/**
 * Applies attention summary filters to a flat list of line items.
 * When multiple filters are active, items must match ANY of the filters (OR logic).
 */
export const filterScopeReportLineItems = (
  lineItems: ScopeReportLineItem[],
  filteredTaskAndGroupIds?: FilteredTaskAndGroupIds,
): ScopeReportLineItem[] => {
  if (!filteredTaskAndGroupIds) return lineItems;
  return lineItems.filter(item => filterScanReportLineItem(item, filteredTaskAndGroupIds));
};

export const getFilteredTasks = (
  scanEditingContext: ScanEditingContextType,
  taskFilterParams: TaskFilterParams,
  scopedOnly?: boolean,
) =>
  getTasksById(
    scanEditingContext,
    getSelectedTasksWithGroupIds(scanEditingContext, taskFilterParams),
    scopedOnly,
  );

export const getFilteredGroups = (
  groups: ScanGroupOptimized[],
  filterRegex?: RegExp,
) =>
  filterRegex
    ? groups.filter(group => filterRegex.test(group.optimized.searchText))
    : groups;
